import endpointMaker from "appredux/endpoints";
import request from "http/request";
import { Horse } from "./types";
import moment from "moment";

export class HorsesService {
  getHorses = async (): Promise<Horse[]> => {
    return request.getJson(
      endpointMaker.api.horsesByFarm(localStorage.getItem("farm")),
      true,
      null
    );
  };

  exportHorsesFromUsef = async (farmcode: string): Promise<any> => {
    const searchParams = new URLSearchParams(location.search);
    const qa_get_data_from_prod = searchParams.get("qa_get_data_from_prod");

    return request.postJson(
      `${endpointMaker.api.baseUrl}/USEF_api.php?farm=${encodeURIComponent(
        farmcode
      )}&task=transferAllHorses&qa_get_data_from_prod=true`,
      "",
      null
    );
  };

  saveHorses = async (horses: Horse[]): Promise<any> => {
    const farmcode = localStorage.getItem("farm");

    return request.postJson(
      `${endpointMaker.api.baseUrl}/horses.php?farm=${encodeURIComponent(
        farmcode
      )}&task=saveHorses`,
      JSON.stringify({
        horses,
      }),
      null
    );
  };

  getHorsesForImport = async (farmcode: string): Promise<any> => {
    return request.postJson(
      `${endpointMaker.api.baseUrl}/USEF_api.php?farm=${encodeURIComponent(
        farmcode
      )}&task=getHorsesForImport&qa_get_data_from_prod=true`,
      "",
      null
    );
  };

  setStatusByHorseIds = async (status: string, horseIds: string[]): Promise<any> => {
    return request.postJson(
      `${endpointMaker.api.horsesByFarm(localStorage.getItem("farm"))}&task=setStatusByHorseIds`,
      JSON.stringify({
        task: "setStatusByHorseIds",
        status,
        horseIds,
      }),
      null
    );
  };

  removeHorse = async (horseId: string): Promise<any> => {
    return request.postJson(
      `${endpointMaker.api.horsesByFarm(localStorage.getItem("farm"))}&task=removeHorse`,
      JSON.stringify({
        horseId,
      }),
      null
    );
  };

  getHorseByUsefId = async (usefId: number): Promise<any> => {
    const farmcode = localStorage.getItem("farm");

    return request.postJson(
      `${endpointMaker.api.baseUrl}/horses.php?farm=${farmcode}&task=getHorseByUsefIdFromUSEF`,
      JSON.stringify({
        usefId,
      }),
      null
    );
  };

  getHorseByEquestrianCanadaId = async (equestrianCanadaId: string): Promise<any> => {
    const farmcode = localStorage.getItem("farm");

    return request.postJson(
      `${endpointMaker.api.baseUrl}/horses.php?farm=${farmcode}&task=getHorseByEquestrianCanadaId`,
      JSON.stringify({
        equestrianCanadaId,
      }),
      null
    );
  };

  /**
   * @param microchip
   * @returns
   * @see - https://qa-api.equitraceapp.com/redoc/#tag/web/operation/ff2db372cb06c45aba745bf5c1f119eb
   */
  leaseHorseForCurrentFarmByMicrochip = async (microchip: string): Promise<any> => {
    return request.postJson(
      `${endpointMaker.api.baseUrl}/horses/relationships/index.php?task=add`,
      JSON.stringify({
        microchip,
        farmcode: localStorage.getItem("farm"),
        role: "leaser",
        expiration_date: 0,
        offproperty: 0,
      }),
      null
    );
  };

  fetchHorseProfile = async (microchip: string): Promise<any> => {
    if (this.horseProfileCache.has(microchip)) {
      return this.horseProfileCache.get(microchip);
    }

    const promise = request
      .getJson(endpointMaker.api.horseProfile(microchip, localStorage.getItem("farm")), true, null)
      .then((data) => {
        const diseases = data.diseases || {};

        data?.healthCertDocuments
          ?.filter((item: any) => item.USEF_status !== "rejected")
          .forEach((item: any) => {
            if (item.HCtype === "CVI") {
              diseases.CVI = item.proofDate;
              diseases["CVI-Exp"] = item.expiry;
            }

            if (item.HCtype === "EECVI") {
              diseases.EECVI = item.proofDate;
              diseases["EECVI-Exp"] = item.expiry;
            }

            if (["Vet Statement", "Veterinary Statement of Health"].includes(item.HCtype)) {
              diseases["Vet Statement"] = item.proofDate;
              diseases["Vet Statement-Exp"] = item.expiry;
            }

            if (item.HCtype === "ecc") {
              diseases.ecc = item.proofDate;
              diseases["ecc-Exp"] = item.expiry;
            }

            if (item.HCtype === "icue") {
              diseases.icue = item.proofDate;
              diseases["icue-Exp"] = item.expiry;
            }

            if (item.HCtype === "fhc") {
              diseases.fhc = item.proofDate;
              diseases["fhc-Exp"] = item.expiry;
            }
          });

        if (diseases.Coggins && !diseases["Coggins-Exp"]) {
          diseases["Coggins-Exp"] = moment.unix(diseases.Coggins).add(1, "year").unix();
        }

        return {
          ...data,
          diseases,
        };
      });

    this.horseProfileCache.set(microchip, promise);

    promise.catch(() => {
      this.horseProfileCache.delete(microchip);
    });

    return promise;
  };

  horseProfileCache = new Map<string, Promise<any>>();

  restGetHorseByMicrochip = async (microchip: string): Promise<any> => {
    const filters: any = {};

    filters.and = [
      {
        offproperty: {
          "!=": 4,
        },
      },
      {
        or: [
          {
            microchip: microchip,
          },
          {
            microchip2: microchip,
          },
          {
            microchip3: microchip,
          },
        ],
      },
    ];

    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filters));

    return request.getJson(
      `${endpointMaker.api.horsesRestEndpoint()}?${params.toString()}`,
      true,
      null
    );
  };

  restGetHorseByUsefId = async (usefId: number): Promise<any> => {
    const filters: any = {};

    filters.and = [
      {
        or: [
          {
            usefId: usefId,
          },
        ],
      },
    ];

    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filters));

    return request.getJson(
      `${endpointMaker.api.horsesRestEndpoint()}?${params.toString()}`,
      true,
      null
    );
  };

  restGetHorseByCanadaId = async (canadaId: string): Promise<any> => {
    const filters: any = {};

    filters.and = [
      {
        or: [
          {
            canadaId: canadaId,
          },
        ],
      },
    ];

    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filters));

    return request.getJson(
      `${endpointMaker.api.horsesRestEndpoint()}?${params.toString()}`,
      true,
      null
    );
  };

  restUpdateHorse = async (horseId: string, horse: Partial<Horse>): Promise<any> => {
    return request.putJson(
      `${endpointMaker.api.horsesRestEndpoint()}?id=${horseId}`,
      JSON.stringify(horse),
      null
    );
  };

  restMergeHorses = async (
    indexHorseMicrochip: string,
    horsesMicrochips: string[]
  ): Promise<any> => {
    return request.postJson(
      `${endpointMaker.api.mergeHorsesRestEndpoint()}`,
      JSON.stringify({ indexHorse: indexHorseMicrochip, horsesMicrochips }),
      null
    );
  };

  restGetHorsesByMicrochips = async (microchips: string[]): Promise<any> => {
    const filters: any = {};

    filters.and = [
      {
        microchip: {
          in: microchips,
        },
      },
    ];

    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filters));
    params.append("perPage", "100");

    return request.getJson(
      `${endpointMaker.api.horsesRestEndpoint()}?${params.toString()}`,
      true,
      null
    );
  };
}

export const horsesService = new HorsesService();
